// TurnierErgebnissePage.jsx — Öffentliche Ergebnisseite (kein Login erforderlich)
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitch from '../components/LanguageSwitch';
import './TurnierErgebnissePage.css';

const MEDAL = { 1: '🥇', 2: '🥈', 3: '🥉' };
const MEDAL_COLOR = { 1: '#d4af37', 2: '#C0C0C0', 3: '#cd7f32' };

export default function TurnierErgebnissePage() {
  const { id } = useParams();
  const { t }  = useTranslation();
  const [turnier, setTurnier]       = useState(null);
  const [kategorien, setKategorien] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [suche, setSuche]           = useState('');
  const [filterTyp, setFilterTyp]   = useState('alle');

  useEffect(() => {
    fetch(`/api/siegerehrung/turnier/${id}/public`)
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setTurnier(d.turnier);
          setKategorien(d.data);
        } else {
          setError(t('results.error_load'));
        }
      })
      .catch(() => setError(t('results.error_conn')))
      .finally(() => setLoading(false));
  }, [id, t]);

  const gefiltert = kategorien.filter(k => {
    if (filterTyp !== 'alle' && k.typ !== filterTyp) return false;
    if (suche && !k.kategorie_name.toLowerCase().includes(suche.toLowerCase()) &&
        !k.plaetze.some(p => `${p.vorname} ${p.nachname}`.toLowerCase().includes(suche.toLowerCase())))
      return false;
    return true;
  });

  const medaillenCount = kategorien.reduce(
    (s, k) => s + k.plaetze.filter(p => p.platzierung <= 3).length, 0
  );

  if (loading) return (
    <div className="tep-page">
      <div className="tep-loading">
        <div className="tep-spinner" />
        <span>{t('results.loading')}</span>
      </div>
    </div>
  );

  if (error) return (
    <div className="tep-page">
      <div className="tep-error">
        <div className="tep-error-icon">⚠</div>
        <p>{error}</p>
        <Link to="/" className="tep-back-btn">{t('results.back')}</Link>
      </div>
    </div>
  );

  return (
    <div className="tep-page">
      {/* Background kanji */}
      <div className="tep-bg-kanji" aria-hidden>武</div>

      {/* Sprachumschalter */}
      <div className="tep-lang-switch">
        <LanguageSwitch />
      </div>

      {/* Header */}
      <header className="tep-header">
        <Link to="/" className="tep-back">{t('results.back')}</Link>
        <div className="tep-header-inner">
          <div className="tep-org">{t('results.org')}</div>
          <h1 className="tep-turnier-name">{turnier?.name}</h1>
          <div className="tep-meta">
            {turnier?.datum && (
              <span>📅 {new Date(turnier.datum).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
            )}
            {turnier?.ort && <span>📍 {turnier.ort}</span>}
            {turnier?.disziplin && <span>🥋 {turnier.disziplin}</span>}
          </div>
          <div className="tep-stats-row">
            <div className="tep-stat">
              <span className="tep-stat-val">{kategorien.length}</span>
              <span className="tep-stat-lbl">{t('results.categories')}</span>
            </div>
            <div className="tep-stat">
              <span className="tep-stat-val">{medaillenCount}</span>
              <span className="tep-stat-lbl">{t('results.medals')}</span>
            </div>
            <div className="tep-stat">
              <span className="tep-stat-val">
                {kategorien.reduce((s, k) => s + k.plaetze.length, 0)}
              </span>
              <span className="tep-stat-lbl">{t('results.placements')}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Filter */}
      <div className="tep-filter">
        <input
          className="tep-search"
          type="text"
          placeholder={t('results.search')}
          value={suche}
          onChange={e => setSuche(e.target.value)}
        />
        <div className="tep-type-btns">
          {[
            ['alle',    t('results.all')],
            ['formen',  t('results.kata')],
            ['bracket', t('results.fight')],
          ].map(([v, l]) => (
            <button
              key={v}
              className={`tep-type-btn ${filterTyp === v ? 'active' : ''}`}
              onClick={() => setFilterTyp(v)}
            >{l}</button>
          ))}
        </div>
        <span className="tep-count">{gefiltert.length} {t('results.categories')}</span>
      </div>

      {/* Ergebnisse */}
      <main className="tep-main">
        {gefiltert.length === 0 ? (
          <div className="tep-empty">{t('results.no_results')}</div>
        ) : (
          <div className="tep-grid">
            {gefiltert.map((kat, i) => (
              <KategorieCard key={i} kat={kat} t={t} />
            ))}
          </div>
        )}
      </main>

      <footer className="tep-footer">
        {t('results.footer', { year: new Date().getFullYear() })}
      </footer>
    </div>
  );
}

function KategorieCard({ kat, t }) {
  const [expanded, setExpanded] = useState(false);
  const top3 = kat.plaetze.filter(p => p.platzierung <= 3).sort((a,b) => a.platzierung - b.platzierung);
  const rest = kat.plaetze.filter(p => p.platzierung > 3).sort((a,b) => a.platzierung - b.platzierung);
  const divInfo = [kat.division_code, kat.division_type].filter(Boolean).join(' · ');

  return (
    <div className="tep-card">
      <div className="tep-card-head">
        <span className={`tep-typ-badge ${kat.typ}`}>
          {kat.typ === 'formen' ? t('results.kata') : t('results.fight')}
        </span>
        <h3 className="tep-kat-name">{kat.kategorie_name}</h3>
        {divInfo && <div className="tep-div-info">{divInfo}</div>}
      </div>

      <div className="tep-podium">
        {[2, 1, 3].map(platz => {
          const p = top3.find(x => x.platzierung === platz);
          return (
            <div key={platz} className={`tep-podium-spot p${platz} ${!p ? 'empty' : ''}`}>
              <div className="tep-medal">{MEDAL[platz] ?? `${platz}.`}</div>
              {p ? (
                <>
                  <div className="tep-athlete-name" style={{ color: platz <= 3 ? MEDAL_COLOR[platz] : '#c4b89a' }}>
                    {p.vorname} {p.nachname}
                  </div>
                  <div className="tep-athlete-club">{p.verein_name}</div>
                  {p.punkte != null && (
                    <div className="tep-athlete-pts">{parseFloat(p.punkte).toFixed(2)} {t('results.points')}</div>
                  )}
                  {p.tie_method && (
                    <div className="tep-athlete-tie">
                      {t(`results.tie_${p.tie_method}`, t('results.tie_break'))}
                    </div>
                  )}
                </>
              ) : (
                <div className="tep-empty-slot">—</div>
              )}
            </div>
          );
        })}
      </div>

      {rest.length > 0 && (
        <>
          <button className="tep-expand-btn" onClick={() => setExpanded(e => !e)}>
            {expanded
              ? t('results.show_less')
              : t(rest.length === 1 ? 'results.show_more' : 'results.show_more_plural', { count: rest.length })
            }
          </button>
          {expanded && (
            <div className="tep-rest">
              {rest.map((p, i) => (
                <div key={i} className="tep-rest-row">
                  <span className="tep-rest-platz">{p.platzierung}.</span>
                  <span className="tep-rest-name">{p.vorname} {p.nachname}</span>
                  <span className="tep-rest-club">{p.verein_name}</span>
                  {p.punkte != null && <span className="tep-rest-pts">{parseFloat(p.punkte).toFixed(2)}</span>}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
